import React, { useState } from 'react';
import { Edit2, Trash2, Save, X } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';

const MaterialCard = ({ material, onUpdate, onDelete }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedSquareFeet, setEditedSquareFeet] = useState(material.squareFeet);

  const handleSave = () => {
    const sqFt = parseFloat(editedSquareFeet);
    if (sqFt > 0) {
      onUpdate(material.id, { squareFeet: sqFt });
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    setEditedSquareFeet(material.squareFeet);
    setIsEditing(false);
  };

  const getCategoryColor = (category) => {
    const colors = {
      'Walls': 'bg-blue-100 text-blue-800 border-blue-200',
      'Roofing': 'bg-amber-100 text-amber-800 border-amber-200',
      'Flooring': 'bg-emerald-100 text-emerald-800 border-emerald-200',
      'Masonry': 'bg-slate-100 text-slate-800 border-slate-200',
      'Structural': 'bg-purple-100 text-purple-800 border-purple-200'
    };
    return colors[category] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  return (
    <Card className="border border-border hover:shadow-md transition-smooth bg-card">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-3">
              <h3 className="text-lg font-semibold text-foreground">{material.name}</h3>
              <Badge variant="outline" className={`${getCategoryColor(material.category)} border`}>
                {material.category}
              </Badge>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Weight/sq ft</p>
                <p className="text-sm font-semibold text-foreground">
                  {material.weightPerSqFt} lbs
                </p>
              </div>
              
              <div>
                <p className="text-xs text-muted-foreground mb-1">Square Feet</p>
                {isEditing ? (
                  <Input
                    type="number"
                    value={editedSquareFeet}
                    onChange={(e) => setEditedSquareFeet(e.target.value)}
                    className="h-8 w-24 text-sm"
                    min="0"
                    step="0.01"
                    autoFocus
                  />
                ) : (
                  <p className="text-sm font-semibold text-foreground">
                    {material.squareFeet.toLocaleString()} sq ft
                  </p>
                )}
              </div>
              
              <div>
                <p className="text-xs text-muted-foreground mb-1">Total Weight</p>
                <p className="text-base font-bold text-primary">
                  {material.totalWeight.toLocaleString(undefined, { maximumFractionDigits: 0 })} lbs
                </p>
              </div>
              
              <div className="flex items-end gap-2">
                {isEditing ? (
                  <>
                    <Button
                      size="sm"
                      onClick={handleSave}
                      className="bg-success hover:bg-success/90 h-8"
                    >
                      <Save className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleCancel}
                      className="h-8"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setIsEditing(true)}
                      className="h-8 hover:bg-accent hover:text-accent-foreground"
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onDelete(material.id)}
                      className="h-8 border-destructive/50 text-destructive hover:bg-destructive/10"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default MaterialCard;